// Hybrid logical clock (docs/architecture/sync-design.md §Clocks).
// Wire format: "<ms:12 hex>-<counter:4 hex>-<node:8 hex>", lowercase, so string order = clock order.

export interface Hlc {
  ms: number;
  counter: number;
  node: string;
}

export const MAX_COUNTER = 0xffff;
/** Remote clocks further ahead of our wall clock than this are rejected (a broken device clock must not win forever). */
export const MAX_DRIFT_MS = 60 * 60 * 1000;
const PATTERN = /^([0-9a-f]{12})-([0-9a-f]{4})-([0-9a-f]{8})$/;

export function encodeHlc(h: Hlc): string {
  return `${h.ms.toString(16).padStart(12, '0')}-${h.counter.toString(16).padStart(4, '0')}-${h.node}`;
}

export function decodeHlc(s: string): Hlc {
  const m = PATTERN.exec(s);
  if (!m) throw new Error(`invalid HLC: ${s}`);
  return {
    ms: Number.parseInt(m[1] as string, 16),
    counter: Number.parseInt(m[2] as string, 16),
    node: m[3] as string,
  };
}

export function compareHlc(a: string, b: string): -1 | 0 | 1 {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Counter overflow rolls into the next millisecond, so the clock never stalls. */
function normalise(ms: number, counter: number, node: string): Hlc {
  return counter > MAX_COUNTER ? { ms: ms + 1, counter: 0, node } : { ms, counter, node };
}

/** The clock for a new local event. */
export function tick(local: Hlc, wallMs: number): Hlc {
  if (wallMs > local.ms) return { ms: wallMs, counter: 0, node: local.node };
  return normalise(local.ms, local.counter + 1, local.node);
}

/** Advance the local clock after seeing a remote one, so later local events sort after it. */
export function receive(local: Hlc, remote: Hlc, wallMs: number): Hlc {
  const ms = Math.max(local.ms, remote.ms, wallMs);
  if (ms === local.ms && ms === remote.ms)
    return normalise(ms, Math.max(local.counter, remote.counter) + 1, local.node);
  if (ms === local.ms) return normalise(ms, local.counter + 1, local.node);
  if (ms === remote.ms) return normalise(ms, remote.counter + 1, local.node);
  return { ms, counter: 0, node: local.node };
}

export function isTooFarAhead(remote: Hlc, wallMs: number, maxDriftMs = MAX_DRIFT_MS): boolean {
  return remote.ms - wallMs > maxDriftMs;
}
